import { useState, useRef, useEffect } from 'react';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import { devLog } from '../utils/devLog';

/**
 * Deploy/pipeline/pods state + handlers, extracted out of Canvas.jsx
 * (DDD refactor Phase 2 — see /Users/giulioroggero/.claude/plans/
 * tender-roaming-bentley.md). Pure move: same state, same handlers, same
 * behavior — only the ownership boundary changed. Depends on
 * `selectedProject`/`selectedRevision` only (both resolved by
 * `useMiaPlatformSelection`, a separate Phase-3 hook not yet extracted —
 * passed in as plain values here, no coupling to how the caller obtains them).
 */
export function usePipelineDeployment(selectedProject, selectedRevision) {
  // Deployment state
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [deploymentError, setDeploymentError] = useState(null);
  const [deploymentPipelineId, setDeploymentPipelineId] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelinePolling, setPipelinePolling] = useState(false);
  const pipelinePollingInterval = useRef(null);

  // Pods status state
  const [showPodsModal, setShowPodsModal] = useState(false);
  const [podsEnvironment, setPodsEnvironment] = useState(null);
  const [podsData, setPodsData] = useState([]);
  const [podsLoading, setPodsLoading] = useState(false);
  const [podsError, setPodsError] = useState(null);

  // Pipeline modal state
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [currentPipelineStatus, setCurrentPipelineStatus] = useState(null);
  const [servicePipelineStatus, setServicePipelineStatus] = useState(null);
  const [showServicePipelineList, setShowServicePipelineList] = useState(false);
  const [servicePipelines, setServicePipelines] = useState([]);
  const [loadingServicePipelines, setLoadingServicePipelines] = useState(false);

  // Listen for service pipeline status updates from GitControls
  useEffect(() => {
    const handleServicePipelineUpdate = (event) => {
      const { status } = event.detail || {};
      setServicePipelineStatus(status);
    };

    window.addEventListener('servicePipelineStatusUpdate', handleServicePipelineUpdate);

    return () => {
      window.removeEventListener('servicePipelineStatusUpdate', handleServicePipelineUpdate);
    };
  }, []);

  const handleDeployClick = (environment) => {
    setSelectedEnvironment(environment);
    setShowDeploymentModal(true);
    setDeploymentError(null);
    setPipelineStatus(null);
    setDeploymentPipelineId(null);
  };

  const startPipelinePolling = (pipelineId) => {
    if (!selectedProject || !selectedEnvironment) return;

    if (!pipelineId) {
      // eslint-disable-next-line no-console
      console.error('❌ Cannot start polling: pipeline ID is missing');
      return;
    }

    setPipelinePolling(true);

    // Clear any existing interval
    if (pipelinePollingInterval.current) {
      clearInterval(pipelinePollingInterval.current);
    }

    // Poll every 5 seconds
    const pollInterval = setInterval(async () => {
      try {
        const status = await miaPlatformDataService.getPipelineStatus(
          selectedProject.id,
          pipelineId,
          selectedEnvironment.envId
        );

        setPipelineStatus(status);

        // Stop polling if pipeline is finished (success, failed, or canceled)
        if (status.status && ['success', 'failed', 'canceled', 'skipped'].includes(status.status.toLowerCase())) {
          clearInterval(pipelinePollingInterval.current);
          setPipelinePolling(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Failed to fetch pipeline status:', error);
        // Continue polling even on error
      }
    }, 5000);

    pipelinePollingInterval.current = pollInterval;
  };

  const handleDeploymentConfirm = async () => {
    // Detailed validation with specific error messages
    if (!selectedProject) {
      setDeploymentError('No project selected. Please select a project first.');
      return;
    }

    if (!selectedProject.id) {
      setDeploymentError('Invalid project data. Missing project ID.');
      return;
    }

    if (!selectedRevision) {
      setDeploymentError('No revision selected. Please select a revision first.');
      return;
    }

    if (!selectedEnvironment) {
      setDeploymentError('No environment selected. Please select an environment first.');
      return;
    }

    try {
      setDeploymentLoading(true);
      setDeploymentError(null);

      // eslint-disable-next-line no-console
      devLog('🚀 Starting deployment:', {
        projectId: selectedProject.id,
        projectName: selectedProject.name,
        environment: selectedEnvironment.envId,
        revision: selectedRevision.id,
      });

      const result = await miaPlatformDataService.triggerDeployment(selectedProject.id, {
        deployType: 'deploy_all',
        environment: selectedEnvironment.envId,
        refType: 'revision',
        revision: selectedRevision.id,
      });

      // eslint-disable-next-line no-console
      devLog('✅ Deployment triggered:', result);

      // Handle different field names for pipeline ID
      const pipelineId = result.pipelineId || result.id;

      if (!pipelineId) {
        throw new Error('Invalid deployment response: missing pipeline ID');
      }

      setDeploymentPipelineId(pipelineId);

      // Start polling for pipeline status
      startPipelinePolling(pipelineId);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Deployment failed:', error);
      setDeploymentError(error.message || 'Failed to trigger deployment');
    } finally {
      setDeploymentLoading(false);
    }
  };

  const handleDeploymentClose = () => {
    setShowDeploymentModal(false);
    setSelectedEnvironment(null);
    setDeploymentError(null);
    setPipelineStatus(null);
    setDeploymentPipelineId(null);

    // Stop polling
    if (pipelinePollingInterval.current) {
      clearInterval(pipelinePollingInterval.current);
      setPipelinePolling(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pipelinePollingInterval.current) {
        clearInterval(pipelinePollingInterval.current);
      }
    };
  }, []);

  // Pods status functions
  const handleViewPodsClick = async (environment) => {
    if (!selectedProject) return;

    setPodsEnvironment(environment);
    setShowPodsModal(true);
    setPodsLoading(true);
    setPodsError(null);
    setPodsData([]);

    try {
      // eslint-disable-next-line no-console
      devLog('📦 Fetching pods for environment:', environment.envId);

      const pods = await miaPlatformDataService.getPodsStatus(
        selectedProject._id,
        environment.envId
      );

      setPodsData(pods || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to fetch pods:', error);
      setPodsError(error.message || 'Failed to fetch pods status');
    } finally {
      setPodsLoading(false);
    }
  };

  const handlePodsModalClose = () => {
    setShowPodsModal(false);
    setPodsEnvironment(null);
    setPodsData([]);
    setPodsError(null);
  };

  return {
    // Deployment
    showDeploymentModal, setShowDeploymentModal,
    selectedEnvironment, setSelectedEnvironment,
    deploymentLoading,
    deploymentError,
    deploymentPipelineId,
    pipelineStatus,
    pipelinePolling,
    handleDeployClick,
    handleDeploymentConfirm,
    handleDeploymentClose,

    // Pods
    showPodsModal,
    podsEnvironment,
    podsData,
    podsLoading,
    podsError,
    handleViewPodsClick,
    handlePodsModalClose,

    // Pipeline modal (currently write-only from the servicePipelineStatusUpdate
    // listener plus their own useState initializers — no other call site sets
    // them; kept as-is, this is a pure move not a dead-code cleanup)
    showPipelineModal, setShowPipelineModal,
    currentPipelineStatus, setCurrentPipelineStatus,
    servicePipelineStatus,
    showServicePipelineList, setShowServicePipelineList,
    servicePipelines, setServicePipelines,
    loadingServicePipelines, setLoadingServicePipelines,
  };
}
